import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiClock, FiSearch, FiCheckCircle, FiSend, FiX } from 'react-icons/fi';
import AppLayout from '../components/layout/AppLayout';
import Button from '../components/common/Button';
import ConfirmDialog from '../components/common/ConfirmDialog';
import Icon from '../components/common/Icon';
import PageHeader from '../components/common/PageHeader';
import StatCard from '../components/common/StatCard';
import SegmentedControl from '../components/common/SegmentedControl';
import EmptyState from '../components/common/EmptyState';
import { IlloPortfolio, IlloSearch } from '../components/common/Illustrations';
import { SkeletonKpiGrid, SkeletonCard } from '../components/common/Skeleton';
import AssignmentCard from '../components/professional/AssignmentCard';
import AccessRequestCard from '../components/professional/AccessRequestCard';
import { professionalAssignmentAPI, companiesAPI } from '../services/api';
import { getView, setView, hasView } from '../services/viewCache';
import { useUI } from '../context/UIContext';
import { markOnboardingStep } from '../utils/onboarding';
import {
  PROFESSIONAL_STATUS_BADGE,
  ASSIGNMENT_SCOPE_BADGE,
  ACCESS_LEVEL_BADGE,
  formatDate,
  formatDateTime,
} from '../components/professional/helpers';

// Stale-while-revalidate cache key (services/viewCache). Filtering is
// client-side, so one stable key holds both fetched lists.
const PRO_DASH_KEY = 'professional:dashboard';

/* ── Filter definitions ───────────────────────────────────────────────── */
const SOURCE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'delegated', label: 'Delegated' },
  { id: 'requested', label: 'Requested' },
];
const STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'active', label: 'Active' },
  { id: 'closed', label: 'Closed' },
];

// Collapses the two source-specific status vocabularies into one bucket so a
// single status filter works across delegations and access requests.
function unifiedStatus(item) {
  if (item.kind === 'assignment') {
    const s = item.raw.professionalStatus;
    return s === 'pending' ? 'pending' : s === 'accepted' ? 'active' : 'closed';
  }
  const s = item.raw.status;
  return s === 'pending' ? 'pending' : s === 'active' ? 'active' : 'closed';
}

function matchesSearch(item, q) {
  if (!q) return true;
  const s = q.toLowerCase();
  if (item.kind === 'assignment') {
    const a = item.raw;
    return [a.assignedBy?.fullName, a.assignedBy?.email, a.assignedBy?.companyName, a.notes]
      .some((v) => (v || '').toLowerCase().includes(s));
  }
  const r = item.raw;
  return [r.company?.name, r.company?.cin, r.owner?.fullName]
    .some((v) => (v || '').toLowerCase().includes(s));
}

/* ── Small UI primitives ──────────────────────────────────────────────── */
function Field({ label, value }) {
  return (
    <div>
      <div className="text-[9.5px] font-bold uppercase tracking-[0.4px]" style={{ color: '#a39a88' }}>
        {label}
      </div>
      <div className="text-[12px] font-semibold mt-0.5" style={{ color: '#08111f' }}>
        {value}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════ */

export default function ProfessionalDashboard() {
  const { showToast } = useUI();
  const [searchParams] = useSearchParams();

  // Stale-while-revalidate: this route unmounts on nav, so both lists refetched
  // from zero on return and flashed a skeleton over cards the pro just saw.
  // Cache them together module-side and render instantly on revisit.
  const cached = getView(PRO_DASH_KEY);
  const [assignments, setAssignments] = useState(() => cached?.assignments ?? []);
  const [requests, setRequests] = useState(() => cached?.requests ?? []);
  const [loading, setLoading] = useState(() => !hasView(PRO_DASH_KEY));

  // ?tab=requests (notification deep-link) pre-filters to requested items.
  const [sourceFilter, setSourceFilter] = useState(
    searchParams.get('tab') === 'requests' ? 'requested' : 'all'
  );
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const [detailAssignment, setDetailAssignment] = useState(null);
  const [pendingAction, setPendingAction] = useState(null); // { type:'accept'|'reject', assignment }
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [remindingId, setRemindingId] = useState(null);

  /* ── Data fetching ──────────────────────────────────────────────────── */
  const fetchAll = useCallback(async () => {
    // Full skeleton only on a true first load; a cached view refreshes silently.
    if (!hasView(PRO_DASH_KEY)) setLoading(true);
    const [aRes, rRes] = await Promise.allSettled([
      professionalAssignmentAPI.getMyProfessionalAssignments({ limit: 100 }),
      companiesAPI.access.myRequests(),
    ]);
    let nextA;
    let nextR;
    if (aRes.status === 'fulfilled' && aRes.value?.success) {
      nextA = aRes.value.data?.assignments || [];
      setAssignments(nextA);
    } else if (aRes.status === 'rejected' && !hasView(PRO_DASH_KEY)) {
      showToast('Failed to load assignments', 'error');
    }
    if (rRes.status === 'fulfilled' && rRes.value?.success) {
      nextR = rRes.value.data || [];
      setRequests(nextR);
    } else if (rRes.status === 'rejected' && !hasView(PRO_DASH_KEY)) {
      showToast('Failed to load access requests', 'error');
    }
    // Cache whatever refreshed this round, preserving the other half so a
    // half-failed refresh never blanks the list that did load.
    if (nextA !== undefined || nextR !== undefined) {
      const prev = getView(PRO_DASH_KEY) || {};
      setView(PRO_DASH_KEY, {
        assignments: nextA !== undefined ? nextA : (prev.assignments || []),
        requests: nextR !== undefined ? nextR : (prev.requests || []),
      });
    }
    setLoading(false);
  }, [showToast]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Honour ?tab=requests arriving via a notification deep-link.
  useEffect(() => {
    if (searchParams.get('tab') === 'requests') setSourceFilter('requested');
  }, [searchParams]);

  /* ── Derived data ───────────────────────────────────────────────────── */
  // Both sources normalised into one date-sorted list.
  const items = useMemo(() => {
    const a = assignments.map((x) => ({
      kind: 'assignment', id: x._id, createdAt: x.createdAt, raw: x,
    }));
    const r = requests.map((x) => ({
      kind: 'request', id: x._id, createdAt: x.createdAt || x.requestedAt, raw: x,
    }));
    return [...a, ...r].sort((x, y) => new Date(y.createdAt) - new Date(x.createdAt));
  }, [assignments, requests]);

  const filtered = useMemo(() => {
    const q = search.trim();
    return items.filter((i) => {
      if (sourceFilter === 'delegated' && i.kind !== 'assignment') return false;
      if (sourceFilter === 'requested' && i.kind !== 'request') return false;
      if (statusFilter !== 'all' && unifiedStatus(i) !== statusFilter) return false;
      if (!matchesSearch(i, q)) return false;
      return true;
    });
  }, [items, sourceFilter, statusFilter, search]);

  const stats = useMemo(() => {
    let needsResponse = 0;
    let active = 0;
    let awaiting = 0;
    for (const a of assignments) {
      if (a.professionalStatus === 'pending') needsResponse += 1;
      else if (a.professionalStatus === 'accepted') active += 1;
    }
    for (const r of requests) {
      if (r.status === 'pending') awaiting += 1;
      else if (r.status === 'active') active += 1;
    }
    return { needsResponse, active, awaiting };
  }, [assignments, requests]);

  // Getting-started runway: an active delegation completes the first pro step.
  useEffect(() => {
    if (stats.active > 0) markOnboardingStep('pro-first-delegation');
  }, [stats.active]);

  /* ── Actions ────────────────────────────────────────────────────────── */
  const askAccept = (assignment) => setPendingAction({ type: 'accept', assignment });
  const askReject = (assignment) => setPendingAction({ type: 'reject', assignment });

  const runPendingAction = async () => {
    if (!pendingAction || isSubmitting) return;
    const { type, assignment } = pendingAction;
    setIsSubmitting(true);
    try {
      const res = type === 'accept'
        ? await professionalAssignmentAPI.acceptAssignment(assignment._id, '')
        : await professionalAssignmentAPI.rejectAssignment(assignment._id, '');
      if (res?.success) {
        showToast(type === 'accept' ? 'Assignment accepted' : 'Assignment rejected', 'success');
        setPendingAction(null);
        setDetailAssignment(null);
        fetchAll();
      } else {
        showToast(res?.message || 'Action could not be completed', 'error');
      }
    } catch (err) {
      showToast(`Failed to ${type} assignment`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemind = async (req) => {
    if (!req.company?._id) return;
    setRemindingId(req._id);
    try {
      const res = await companiesAPI.access.remind(req.company._id, req._id);
      if (res?.success) {
        showToast(res.message || 'Reminder sent to the company owner', 'success');
        setRequests((prev) => prev.map((r) => (
          r._id === req._id
            ? {
                ...r,
                lastRemindedAt: res.data?.lastRemindedAt || new Date().toISOString(),
                reminderCount: res.data?.reminderCount ?? (r.reminderCount || 0) + 1,
              }
            : r
        )));
      } else {
        showToast(res?.message || 'Could not send reminder', 'error');
      }
    } catch (err) {
      showToast(err?.message || 'Could not send reminder', 'error');
    } finally {
      setRemindingId(null);
    }
  };

  /* ── Render ─────────────────────────────────────────────────────────── */
  const summaryTiles = [
    { label: 'Needs your response', value: stats.needsResponse, color: '#8a6a1f', hint: 'Delegations to accept or reject', icon: FiClock },
    { label: 'Active access', value: stats.active, color: '#4a6b3f', hint: 'Companies you can work on now', icon: FiCheckCircle },
    { label: 'Awaiting approval', value: stats.awaiting, color: '#836016', hint: 'Your requests in review', icon: FiSend },
  ];

  return (
    <AppLayout pageTitle="Professional Dashboard">
      <PageHeader
        title="Professional Dashboard"
        subtitle="Every company you can work on — delegated to you by clients or requested by you — in one place."
      />

      {loading ? (
        <>
          <SkeletonKpiGrid count={3} className="mb-5" />
          <SkeletonCard rows={4} />
        </>
      ) : (
        <>
          {/* Pending-delegations banner */}
          {stats.needsResponse > 0 && (
            <div
              className="mb-5 px-4 py-3 rounded-[10px] border flex items-start gap-3"
              style={{ background: '#f3ebd7', borderColor: '#e7d7ad', color: '#8a6a1f' }}
            >
              <FiClock size={17} className="mt-[1px] shrink-0" />
              <div className="flex-1">
                <div className="text-[13px] font-semibold">
                  {stats.needsResponse} delegation{stats.needsResponse === 1 ? '' : 's'} awaiting your response.
                </div>
                <div className="text-[12px] mt-1 leading-relaxed">
                  {"Until you accept a delegation, that client's companies and drafts won't appear elsewhere in the app."}
                </div>
              </div>
              <button
                onClick={() => { setStatusFilter('pending'); setSourceFilter('all'); }}
                className="text-[11.5px] font-semibold px-3 py-1.5 rounded-md shrink-0 transition-colors hover:bg-white/60"
                style={{ background: 'rgba(255,255,255,0.5)', color: '#8a6a1f', border: '1px solid #e7d7ad' }}
              >
                Show pending →
              </button>
            </div>
          )}

          {/* Summary tiles */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
            {summaryTiles.map((tile) => (
              <StatCard
                key={tile.label}
                label={tile.label}
                value={tile.value}
                sub={tile.hint}
                tone={tile.color}
                icon={tile.icon}
                compact
              />
            ))}
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <SegmentedControl
              options={SOURCE_FILTERS.map((o) => ({ key: o.id, label: o.label }))}
              value={sourceFilter}
              onChange={setSourceFilter}
            />
            <SegmentedControl
              options={STATUS_FILTERS.map((o) => ({ key: o.id, label: o.label }))}
              value={statusFilter}
              onChange={setStatusFilter}
            />
            <div className="flex-1 min-w-[180px] max-w-sm relative">
              <FiSearch
                size={13}
                className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                style={{ color: '#a39a88' }}
              />
              <input
                className="pl-9 pr-3 py-2 w-full rounded-[8px] border text-[12.5px] focus:outline-none focus:ring-2 focus:ring-[#145f5f]"
                style={{ borderColor: '#ddd6c6' }}
                placeholder="Search by client, company, CIN or owner…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Unified list — three-state empties */}
          {filtered.length === 0 ? (
            items.length === 0 ? (
              <EmptyState
                variant="hero"
                illustration={<IlloPortfolio />}
                title="Your client work starts here"
                subtitle="Delegations from corporate clients and the company access you request both land on this page — accept, track and follow up in one place. Ask your client to add you as a collaborator, or request access to a company."
              />
            ) : (
              <EmptyState
                variant="filter"
                illustration={<IlloSearch />}
                title="No items match your filters"
                subtitle="Try a different source, status or search term."
                action={(
                  <Button variant="outline" size="sm" onClick={() => { setSourceFilter('all'); setStatusFilter('all'); setSearch(''); }}>
                    Clear filters
                  </Button>
                )}
              />
            )
          ) : (
            <div className="space-y-3">
              {filtered.map((item) => (
                item.kind === 'assignment' ? (
                  <AssignmentCard
                    key={item.id}
                    assignment={item.raw}
                    onAccept={askAccept}
                    onReject={askReject}
                    onViewDetails={setDetailAssignment}
                  />
                ) : (
                  <AccessRequestCard
                    key={item.id}
                    req={item.raw}
                    onRemind={handleRemind}
                    reminding={remindingId === item.id}
                  />
                )
              ))}
            </div>
          )}
        </>
      )}

      {/* ── Assignment details modal ───────────────────────────────────── */}
      {detailAssignment && (() => {
        const a = detailAssignment;
        const st = PROFESSIONAL_STATUS_BADGE[a.professionalStatus] || PROFESSIONAL_STATUS_BADGE.pending;
        const companies = (a.assignedCompanies || [])
          .map((ac) => ({ data: ac.company, level: ac.accessLevel }))
          .filter((c) => c.data && c.data._id);
        const isPending = a.professionalStatus === 'pending';
        return (
          <div
            className="fixed inset-0 z-[1090] flex items-center justify-center p-4"
            style={{ background: 'rgba(8,17,31,0.55)' }}
            onClick={(e) => { if (e.target === e.currentTarget) setDetailAssignment(null); }}
          >
            <div
              className="bg-white rounded-2xl w-full max-w-lg max-h-[88vh] overflow-y-auto"
              style={{ boxShadow: '0 16px 48px rgba(8,17,31,0.2)' }}
            >
              <div
                className="flex items-center justify-between px-5 py-3.5 border-b sticky top-0 bg-white"
                style={{ borderColor: '#eee6d8' }}
              >
                <h2
                  className="text-[16px] font-bold"
                  style={{ color: '#08111f', fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Assignment details
                </h2>
                <button
                  type="button"
                  aria-label="Close"
                  title="Close"
                  onClick={() => setDetailAssignment(null)}
                  className="group w-8 h-8 rounded-[10px] flex items-center justify-center text-[#7a7060] transition-all duration-200 hover:bg-[#9a564a]/10 hover:text-[#9a564a] active:scale-90"
                >
                  <FiX size={17} className="transition-transform duration-300 group-hover:rotate-90" />
                </button>
              </div>

              <div className="p-5">
                {/* Client + status */}
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[15px] font-bold" style={{ color: '#08111f' }}>
                      {a.assignedBy?.fullName || 'Unknown client'}
                    </div>
                    <div className="text-[11.5px] mt-0.5 truncate" style={{ color: '#7a7060' }}>
                      {a.assignedBy?.email || '—'}
                      {a.assignedBy?.companyName ? ` · ${a.assignedBy.companyName}` : ''}
                    </div>
                  </div>
                  <span
                    className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10.5px] font-bold"
                    style={{ background: st.bg, color: st.color }}
                  >
                    <Icon name={st.icon} size={11} /> {st.label}
                  </span>
                </div>

                {/* Field grid */}
                <div
                  className="grid grid-cols-2 gap-x-4 gap-y-3 mt-4 pt-4 border-t"
                  style={{ borderColor: '#f0ebdf' }}
                >
                  <Field
                    label="Access level"
                    value={(ACCESS_LEVEL_BADGE[a.accessLevel] || {}).label || a.accessLevel}
                  />
                  <Field
                    label="Scope"
                    value={(ASSIGNMENT_SCOPE_BADGE[a.assignmentScope] || {}).label || a.assignmentScope}
                  />
                  <Field label="Delegated on" value={formatDateTime(a.createdAt)} />
                  <Field label="Valid from" value={formatDate(a.validFrom)} />
                  <Field label="Valid until" value={a.validUntil ? formatDate(a.validUntil) : 'No expiry'} />
                  {a.professionalResponseAt && (
                    <Field label="Responded on" value={formatDateTime(a.professionalResponseAt)} />
                  )}
                </div>

                {/* Notes */}
                {a.notes && (
                  <div className="mt-4 pt-4 border-t" style={{ borderColor: '#f0ebdf' }}>
                    <div className="text-[9.5px] font-bold uppercase tracking-[0.4px]" style={{ color: '#a39a88' }}>
                      Note from client
                    </div>
                    <div className="text-[12px] mt-1 leading-relaxed" style={{ color: '#08111f' }}>
                      {a.notes}
                    </div>
                  </div>
                )}
                {a.professionalResponseNotes && (
                  <div className="mt-3">
                    <div className="text-[9.5px] font-bold uppercase tracking-[0.4px]" style={{ color: '#a39a88' }}>
                      Your response note
                    </div>
                    <div className="text-[12px] mt-1 leading-relaxed" style={{ color: '#08111f' }}>
                      {a.professionalResponseNotes}
                    </div>
                  </div>
                )}

                {/* Assigned companies */}
                {a.assignmentScope === 'specific_companies' && (
                  <div className="mt-4 pt-4 border-t" style={{ borderColor: '#f0ebdf' }}>
                    <div
                      className="text-[9.5px] font-bold uppercase tracking-[0.4px] mb-2"
                      style={{ color: '#a39a88' }}
                    >
                      Assigned companies ({companies.length})
                    </div>
                    {companies.length === 0 ? (
                      <div className="text-[11.5px]" style={{ color: '#a39a88' }}>
                        No companies listed.
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        {companies.map((c) => (
                          <div
                            key={c.data._id}
                            className="flex items-center justify-between gap-2 px-3 py-2 rounded-[8px]"
                            style={{ background: '#f8f5ef' }}
                          >
                            <div className="min-w-0">
                              <div className="text-[12px] font-semibold truncate" style={{ color: '#08111f' }}>
                                {c.data.name}
                              </div>
                              <div
                                className="text-[10px]"
                                style={{ color: '#7a7060', fontFamily: "'JetBrains Mono', monospace" }}
                              >
                                {c.data.cin || '—'}
                              </div>
                            </div>
                            <span
                              className="text-[10px] font-semibold px-2 py-[2px] rounded shrink-0"
                              style={{ background: '#fff', color: '#7a7060', border: '1px solid #ddd6c6' }}
                            >
                              {(ACCESS_LEVEL_BADGE[c.level] || {}).label || c.level}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Footer */}
                <div className="flex items-center gap-2 mt-5 pt-4 border-t" style={{ borderColor: '#f0ebdf' }}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDetailAssignment(null)}
                  >
                    Close
                  </Button>
                  {isPending && (
                    <>
                      <Button
                        variant="danger"
                        size="sm"
                        className="ml-auto"
                        onClick={() => { askReject(a); setDetailAssignment(null); }}
                      >
                        Reject
                      </Button>
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => { askAccept(a); setDetailAssignment(null); }}
                      >
                        Accept
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Accept / Reject confirmation ───────────────────────────────── */}
      <ConfirmDialog
        open={!!pendingAction}
        onClose={() => { if (!isSubmitting) setPendingAction(null); }}
        onConfirm={runPendingAction}
        variant={pendingAction?.type === 'reject' ? 'danger' : 'default'}
        title={pendingAction?.type === 'reject' ? 'Reject this assignment?' : 'Accept this assignment?'}
        message={
          pendingAction?.type === 'reject'
            ? `This declines the delegation from ${pendingAction?.assignment?.assignedBy?.fullName || 'the client'}. They will be notified.`
            : `Accepting gives you access to the companies and documents delegated by ${pendingAction?.assignment?.assignedBy?.fullName || 'the client'}.`
        }
        note={
          pendingAction?.type === 'reject'
            ? 'The client can re-send the delegation later if needed.'
            : 'You can be removed from this delegation later by the client.'
        }
        confirmLabel={pendingAction?.type === 'reject' ? 'Reject assignment' : 'Accept assignment'}
        cancelLabel="Cancel"
        loading={isSubmitting}
      />
    </AppLayout>
  );
}
